import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Card,
  CardActionArea,
  Box,
  IconButton,
  Fade,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import TuneOutlinedIcon from '@material-ui/icons/TuneOutlined';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import { translate } from 'containers/services/translate';
import messages from 'containers/IdentificationPage/messages';
import { injectIntl, IntlShape } from 'react-intl';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    borderRadius: 20,
    padding: theme.spacing(1),
    overflow: 'hidden',
    boxShadow: '0 24px 80px rgba(15, 23, 42, 0.20)',
    minWidth: 720,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
      width: 'calc(100% - 24px)',
      margin: 12,
    },
  },
  titleRoot: {
    padding: theme.spacing(3, 3, 1.5, 3),
  },
  titleRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  titleText: {
    fontWeight: 700,
    fontSize: 24,
    color: '#0F172A',
  },
  subtitle: {
    marginTop: theme.spacing(1),
    color: '#64748B',
    fontSize: 14,
    lineHeight: 1.6,
  },
  closeBtn: {
    color: '#64748B',
    padding: 8,
  },
  content: {
    padding: theme.spacing(2, 3, 3, 3),
  },
  optionCard: {
    borderRadius: 18,
    border: '1px solid #E2E8F0',
    boxShadow: '0 8px 24px rgba(15, 23, 42, 0.05)',
    transition: 'all 180ms ease',
    overflow: 'hidden',
    background: '#FFFFFF',
    position: 'relative',
    height: '100%',
    '&:hover': {
      transform: 'translateY(-3px)',
      boxShadow: '0 16px 38px rgba(15, 23, 42, 0.10)',
      borderColor: '#90CAF9',
    },
  },
  optionCardSelected: {
    border: '2px solid #1976D2',
    background: 'linear-gradient(180deg, #F8FBFF 0%, #EEF6FF 100%)',
    boxShadow: '0 16px 40px rgba(25, 118, 210, 0.16)',
  },
  optionActionArea: {
    height: '100%',
  },
  optionInner: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
  },
  topRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: theme.spacing(2),
  },
  iconWrapBlue: {
    width: 52,
    height: 52,
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #E3F2FD 0%, #F1F8FF 100%)',
    color: '#1976D2',
  },
  iconWrapPurple: {
    width: 52,
    height: 52,
    borderRadius: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(135deg, #EDE9FE 0%, #F7F3FF 100%)',
    color: '#7C3AED',
  },
  checkIcon: {
    color: '#1976D2',
    fontSize: 22,
  },
  cardTitle: {
    fontWeight: 700,
    fontSize: 18,
    color: '#0F172A',
    marginBottom: theme.spacing(1),
  },
  cardText: {
    color: '#64748B',
    fontSize: 14,
    lineHeight: 1.7,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 10px',
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    marginTop: theme.spacing(2),
    width: 'fit-content',
  },
  chipBlue: {
    background: '#E3F2FD',
    color: '#1565C0',
  },
  chipPurple: {
    background: '#F3E8FF',
    color: '#7C3AED',
  },
  actions: {
    padding: theme.spacing(0, 3, 3, 3),
    justifyContent: 'space-between',
  },
  leftAction: {
    color: '#64748B',
    fontWeight: 600,
    textTransform: 'none',
  },
  primaryBtn: {
    minWidth: 140,
    height: 44,
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: 14,
    boxShadow: 'none',
    color: 'white',
    background: 'linear-gradient(135deg, #1D4ED8 0%, #2563EB 100%)',
    '&:hover': {
      boxShadow: 'none',
      background: 'linear-gradient(135deg, #1E40AF 0%, #1D4ED8 100%)',
    },
  },
}));

export interface PreOuvertureModalProps {
  open: boolean;
  onClose: any;
  onContinue: Function;
  intl: IntlShape;
}
const PreOuvertureModal: React.FC<PreOuvertureModalProps> = (
  props,
): JSX.Element => {
  const classes = useStyles();
  const [op, setOption] = useState(null);

  const options = [
    {
      value: 'normal',
      title: translate(messages.normalTypeTitle, props),
      icon: <DescriptionOutlinedIcon fontSize="medium" />,
    },
    {
      value: 'usage',
      title: translate(messages.usageTypeTitle, props),
      icon: <TuneOutlinedIcon fontSize="medium" />,
    },
  ];
  const onChange = value => {
    setOption(value);
  };

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      PaperProps={{ className: classes.dialogPaper }}
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(15, 23, 42, 0.45)',
          backdropFilter: 'blur(3px)',
        },
      }}
    >
      <DialogTitle disableTypography className={classes.titleRoot}>
        <div className={classes.titleRow}>
          <div>
            <Typography className={classes.titleText}>
              {translate(messages.dialogTitle, props)}
            </Typography>
            <Typography className={classes.subtitle}>
              {translate(messages.subTitleDialog, props)}
            </Typography>
          </div>

          <IconButton onClick={props.onClose} className={classes.closeBtn}>
            <CloseRoundedIcon />
          </IconButton>
        </div>
      </DialogTitle>

      <DialogContent className={classes.content}>
        <Grid container spacing={2}>
          {options.map((option, index) => {
            const selected = op === option.value;

            return (
              <Grid item xs={12} md={6} key={option.value}>
                <Card
                  className={`${classes.optionCard} ${
                    selected ? classes.optionCardSelected : ''
                  }`}
                  elevation={0}
                >
                  <CardActionArea
                    className={classes.optionActionArea}
                    onClick={() => onChange(option.value)}
                  >
                    <Box className={classes.optionInner}>
                      <Box className={classes.topRow}>
                        <Box
                          className={
                            index === 0
                              ? classes.iconWrapBlue
                              : classes.iconWrapPurple
                          }
                        >
                          {option.icon}
                        </Box>

                        {selected ? (
                          <CheckCircleIcon className={classes.checkIcon} />
                        ) : null}
                      </Box>

                      <Typography className={classes.cardTitle}>
                        {option.title}
                      </Typography>
                    </Box>
                  </CardActionArea>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button onClick={props.onClose} className={classes.leftAction}>
          {translate(messages.cancelButtonTitle, props)}
        </Button>

        <Button
          variant="contained"
          color="primary"
          className={classes.primaryBtn}
          disabled={!op}
          onClick={() => props.onContinue(op)}
        >
          {translate(messages.confirmButtonTitle, props)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
export default injectIntl(PreOuvertureModal);
